/**
 * What a stored series is, and what may be drawn from it (REQ-021).
 *
 * `/trend` serves every stored day, `unavailable` ones in place, precisely so
 * the screen has to decide for itself how to cross a gap. This file makes that
 * decision once, in a pure function, so the drawing code cannot quietly make a
 * different one: a day nobody measured never becomes a point, never becomes a
 * zero, and never becomes a segment of line joining the days either side of it.
 * A line drawn through an outage is a claim about a period nobody looked at.
 *
 * A day the snapshot job never ran has no row to serve, and it is the more
 * dangerous of the two absences: an `unavailable` row is written by a job that
 * ran and found nothing, whereas a missing row means nobody looked at all —
 * the backend was down, the replicas were restarting, or the metric entered the
 * registry later. `fillWindow` materialises the calendar so those days exist as
 * days rather than as nothing, and every x below is derived from a date rather
 * than from a position in an array, which is how a missing day used to close up
 * behind itself and leave an unbroken line labelled as fully measured.
 *
 * Nothing here computes a projection. REQ-021's non-goals rule out forecasting,
 * and `MIN_TREND_POINTS` is the same refusal at the other end of the scale: two
 * readings are two readings, and calling them a trend is the AC-005 failure —
 * thin data presented as evidence — with a line drawn through it.
 */
import { HttpError } from "@/lib/http";
import type { MetricDescription, MetricValue } from "./metrics";

/**
 * How much history the sparkline asks for. Short on purpose: a mark this size
 * cannot resolve the backend's 90-day default, and ten figures each pulling
 * three months would be paid for in bytes nobody can see.
 */
export const TREND_DAYS = 30;

/**
 * Fewer measured days than this is not a trend, and is said in words instead.
 *
 * A week, because the snapshot is daily and a shape shorter than a week is one
 * or two readings with a slope drawn between them.
 */
export const MIN_TREND_POINTS = 7;

export interface TrendPoint {
  date: string;
  value: MetricValue;
  sampleSize: number;
  /** The metric's `definedOn` as it stood the day this point was written. */
  definedOn: string;
}

export interface TrendPayload {
  metric: MetricDescription;
  points: TrendPoint[];
}

export interface TrendMeta {
  computedAt: string;
  days: number;
  count: number;
  unavailableDays: number;
  definedOn: string;
  definitionChanged: boolean;
}

/**
 * One day of the window, stored or not.
 *
 * `unrecorded` is this file's own status and never arrives from the server: it
 * is the absence of a row, which the response can only express by saying
 * nothing. `definedOn` is therefore optional — a day nobody wrote carries no
 * definition, and inventing the neighbouring one would fake a continuity the
 * series does not have.
 */
export interface TrendDay {
  date: string;
  value: MetricValue | { status: "unrecorded" };
  sampleSize: number;
  definedOn?: string;
}

/** A day that has a value, and where on the axis it sits. */
export interface TrendMark {
  /** Days since the start of the window — a date, not a position in an array. */
  index: number;
  value: number;
}

export interface TrendShape {
  /** Every day of the window, one entry per calendar day, oldest first. */
  days: TrendDay[];
  /** How many days the window spans. `days.length`, named for the caption. */
  span: number;
  /** Days with a value: the only days that become a point on the line. */
  measured: number;
  /** Days the source was gone. Drawn as a gap, never crossed. */
  gaps: number;
  /** Days that ran under their denominator. Neither a value nor an absence. */
  thin: number;
  /** Days with no row at all: the job did not run. Also a gap, and louder. */
  unrecorded: number;
  min: number;
  max: number;
  /**
   * Runs of consecutive measured days. One run is one stroke; a run of a single
   * day is a dot, since a one-point line is nothing at all. The value is
   * carried rather than looked up again, so no drawing code can reach for the
   * number on a day that has none.
   */
  segments: TrendMark[][];
  /** Whether there is enough measured history to draw anything (AC-005). */
  enough: boolean;
  /**
   * The first day whose definition differs from the day before it — where the
   * series stops being one series (`meta.definitionChanged` exists for this).
   */
  changeIndex?: number;
  /**
   * The definition moved after every stored day was written, so the boundary is
   * off the right-hand edge rather than inside the series. Worth saying and
   * impossible to mark.
   */
  changedAfterSeries: boolean;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** `YYYY-MM-DD` as a count of UTC days, so day arithmetic is integer. */
function dayNumber(date: string): number {
  return (
    Date.UTC(
      Number(date.slice(0, 4)),
      Number(date.slice(5, 7)) - 1,
      Number(date.slice(8, 10)),
    ) / DAY_MS
  );
}

function dateOfDay(day: number): string {
  return new Date(day * DAY_MS).toISOString().slice(0, 10);
}

/**
 * The stored rows laid back onto the calendar they were written on.
 *
 * The window ends the day the answer was computed, not the day of the last
 * stored row: a job that stopped a fortnight ago has its silence at the
 * right-hand edge, and clamping the window to the last row is exactly how that
 * silence would disappear. It begins at the first stored row rather than at
 * `meta.days` ago, because nothing before the first row is knowable from here —
 * the metric may have entered the registry that morning, and reporting three
 * unrecorded weeks before it existed would cry wolf on every new metric.
 */
export function fillWindow(points: TrendPoint[], meta?: TrendMeta): TrendDay[] {
  const first = points[0];
  const lastStored = points[points.length - 1];
  if (!first || !lastStored) return [];

  const computedDay = meta?.computedAt
    ? dayNumber(meta.computedAt.slice(0, 10))
    : Number.NEGATIVE_INFINITY;

  const start = dayNumber(first.date);
  const end = Math.max(dayNumber(lastStored.date), computedDay);

  const stored = new Map(points.map((point) => [point.date, point]));
  const days: TrendDay[] = [];
  for (let day = start; day <= end; day += 1) {
    const date = dateOfDay(day);
    days.push(
      stored.get(date) ?? {
        date,
        value: { status: "unrecorded" },
        sampleSize: 0,
      },
    );
  }
  return days;
}

/** The shape of a series, decided once so the drawing cannot decide otherwise. */
export function readTrend(points: TrendPoint[], meta?: TrendMeta): TrendShape {
  const days = fillWindow(points, meta);
  const segments: TrendMark[][] = [];
  let run: TrendMark[] = [];
  let measured = 0;
  let gaps = 0;
  let thin = 0;
  let unrecorded = 0;
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  let changeIndex: number | undefined;
  // The last definition actually written down. An unrecorded day carries none,
  // and comparing against nothing would report a definition change on the day
  // the job came back.
  let lastDefinedOn: string | undefined;

  days.forEach((day, index) => {
    if (day.definedOn !== undefined) {
      if (lastDefinedOn !== undefined && day.definedOn !== lastDefinedOn) {
        changeIndex ??= index;
      }
      lastDefinedOn = day.definedOn;
    }

    if (day.value.status === "ok") {
      measured += 1;
      min = Math.min(min, day.value.value);
      max = Math.max(max, day.value.value);
      run.push({ index, value: day.value.value });
      return;
    }

    if (day.value.status === "unavailable") gaps += 1;
    else if (day.value.status === "unrecorded") unrecorded += 1;
    else thin += 1;

    // The run ends here rather than reaching over the day: this is the whole
    // reason the backend keeps absent days in place.
    if (run.length > 0) segments.push(run);
    run = [];
  });
  if (run.length > 0) segments.push(run);

  return {
    days,
    span: days.length,
    measured,
    gaps,
    thin,
    unrecorded,
    min: measured > 0 ? min : 0,
    max: measured > 0 ? max : 0,
    segments,
    enough: measured >= MIN_TREND_POINTS,
    changeIndex,
    changedAfterSeries:
      changeIndex === undefined && (meta?.definitionChanged ?? false),
  };
}

/* --------------------------------------------------------------- geometry */

/** The sparkline's box. Small enough that ten of them stay one screen. */
export const SPARK = {
  width: 140,
  height: 32,
  /** Room for the end marker and its surface ring at either extreme. */
  padY: 6,
  /**
   * The same room sideways. Without it the first and last day's band, and the
   * end marker, are cut in half by the edge of the box — an absence on the
   * first or last day of the window is the one a reader most needs to see whole.
   */
  padX: 4,
} as const;

/** The drawable width once both margins are taken out. */
const plotWidth = SPARK.width - 2 * SPARK.padX;

/** Where a day sits, by its distance in days from the start of the window. */
export function xAt(index: number, count: number): number {
  if (count <= 1) return SPARK.width / 2;
  return SPARK.padX + (index / (count - 1)) * plotWidth;
}

export function yAt(value: number, min: number, max: number): number {
  const top = SPARK.padY;
  const bottom = SPARK.height - SPARK.padY;
  // A flat series sits on the middle rather than the floor: drawing every equal
  // value along the bottom would read as zero, which is the one number this
  // screen must never invent.
  if (max === min) return (top + bottom) / 2;
  return bottom - ((value - min) / (max - min)) * (bottom - top);
}

/** The width of one day's band, never so thin that a gap becomes invisible. */
export function bandWidth(count: number): number {
  if (count <= 1) return SPARK.width;
  return Math.max(plotWidth / (count - 1), 3);
}

/* ------------------------------------------------------- when it cannot be */

/**
 * Why the series could not be read — never "no data".
 *
 * A 404 says the registry has no such metric and a 400 says this screen asked
 * wrongly; both are defects with owners, and rendering either as an empty
 * history would file them under "nothing has happened yet", where nobody looks.
 */
/**
 * A 200 whose body carries no series. Named rather than treated as an empty
 * history for the same reason as the failures below: a response the screen
 * could not read is a defect with an owner, and "no points" is what a metric
 * with no history looks like.
 */
export const MALFORMED_TREND =
  "The server answered without a series in it. Nothing could be read, and a history of any shape would look exactly like this.";

export function trendFailure(error: unknown): string {
  const status =
    error instanceof HttpError ? error.response?.status : undefined;

  if (status === 404) {
    return "The server has no metric defined under this id, so no series was ever kept for it. That is a gap in the metric registry, not an empty history.";
  }
  if (status === 400) {
    return "The series was requested without naming a metric. Nothing was read, and the fault is on this screen rather than in the data.";
  }
  return "The stored series could not be read. That is not the same as having no history — nothing was read at all, and a trend of any shape would look exactly like this.";
}
